// WPS-028 · Wunli Showcase · public atelier gallery
// Opt-in clients are featured here · social proof + traffic driver.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { showcaseApi } from '../../services/api';
import { Star, ExternalLink, Briefcase, Sparkles } from 'lucide-react';

const INDUSTRY_FILTERS = [
  { slug: null,                 label: 'All' },
  { slug: 'rim-repair',         label: 'Rim Repair' },
  { slug: 'auto-glass-repair',  label: 'Auto Glass' },
  { slug: 'auto-body-paint',    label: 'Body & Paint' },
  { slug: 'plumbing',           label: 'Plumbing' },
  { slug: 'lawn-care',          label: 'Lawn Care' },
  { slug: 'house-cleaning',     label: 'Cleaning' },
  { slug: 'electrical',         label: 'Electrical' },
  { slug: 'hvac-service',       label: 'HVAC' },
];

const ShowcaseGallery = () => {
  const [items, setItems] = useState([]);
  const [industry, setIndustry] = useState(null);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [industry, featuredOnly]);

  const load = async () => {
    setLoading(true);
    const r = await showcaseApi.list(industry, featuredOnly);
    if (r?.success) setItems(r.data.items || []);
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Hero noir */}
      <div style={{ background: '#0a0a0a', color: '#ffffff', padding: '40px 20px 60px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.2em', color: '#c9a961', marginBottom: '12px' }}>
            WUNLI · ATELIER SHOWCASE
          </div>
          <div style={{ fontSize: '32px', fontWeight: 800, letterSpacing: '-0.02em', marginBottom: '8px' }}>
            Real cards · real businesses
          </div>
          <div style={{ fontSize: '14px', color: '#94a3b8', maxWidth: '560px', lineHeight: 1.5 }}>
            A curated gallery of atelier-grade business cards designed and printed by Wunli for local service professionals. Every Smart Card is live and tappable.
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '20px 16px' }}>
        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
          {INDUSTRY_FILTERS.map(f => (
            <button key={f.label} onClick={() => setIndustry(f.slug)} style={{
              padding: '6px 12px',
              fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
              letterSpacing: '0.12em', textTransform: 'uppercase',
              border: '1px solid',
              borderColor: industry === f.slug ? '#0f172a' : '#e2e8f0',
              background: industry === f.slug ? '#0f172a' : '#ffffff',
              color: industry === f.slug ? '#ffffff' : '#0f172a',
              cursor: 'pointer',
            }}>{f.label}</button>
          ))}
          <button onClick={() => setFeaturedOnly(!featuredOnly)} style={{
            padding: '6px 12px',
            fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
            letterSpacing: '0.12em', textTransform: 'uppercase',
            border: '1px solid #c9a961',
            background: featuredOnly ? '#c9a961' : '#ffffff',
            color: featuredOnly ? '#0a0a0a' : '#0a0a0a',
            cursor: 'pointer',
            marginLeft: 'auto',
          }}>
            <Star className="h-3 w-3 inline mr-1" /> Featured
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: '#64748b' }}>
            Loading showcase…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: '#ffffff', border: '1px dashed #cbd5e1' }}>
            <Briefcase className="h-12 w-12" style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>No cards in this filter yet</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Check back soon or browse all industries.</div>
          </div>
        )}

        {/* Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
          {items.map(item => (
            <Link key={item.slug} to={`/sc/${item.slug}?ref=showcase`} style={{ textDecoration: 'none' }}>
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', overflow: 'hidden', transition: 'transform 0.2s ease, border-color 0.2s ease', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = '#01579b'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              >
                {/* Card visual placeholder noir */}
                <div style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)', height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <div style={{ color: '#c9a961', fontSize: '40px', fontWeight: 800, letterSpacing: '-0.02em' }}>
                    {(item.fullName || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                  {item.featured && (
                    <div style={{ position: 'absolute', top: 8, right: 8, padding: '2px 8px', background: '#c9a961', color: '#0a0a0a', fontSize: '9px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 800 }}>
                      <Star className="h-3 w-3 inline mr-1" /> Featured
                    </div>
                  )}
                </div>
                <div style={{ padding: '14px' }}>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '2px' }}>{item.fullName}</div>
                  {item.jobTitle && (
                    <div style={{ fontSize: '12px', color: '#01579b', marginBottom: '2px' }}>{item.jobTitle}</div>
                  )}
                  {item.companyName && (
                    <div style={{ fontSize: '13px', color: '#475569', fontWeight: 500, marginBottom: '8px' }}>{item.companyName}</div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                    {item.industry && (
                      <div style={{ fontSize: '9px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#94a3b8' }}>
                        {item.industry.replace(/-/g, ' ')}
                      </div>
                    )}
                    <ExternalLink className="h-3 w-3" style={{ color: '#94a3b8' }} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Footer CTA */}
        <div style={{ marginTop: '40px', textAlign: 'center', padding: '32px 20px', background: '#ffffff', border: '1px solid #e2e8f0' }}>
          <Sparkles className="h-6 w-6" style={{ color: '#c9a961', margin: '0 auto 8px' }} />
          <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Your card here next</div>
          <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>Atelier-grade design · printed on premium stock · Smart Card included.</div>
          <Link to="/signup" style={{ display: 'inline-block', padding: '12px 24px', background: '#01579b', color: '#ffffff', textDecoration: 'none', fontSize: '13px', fontWeight: 700, letterSpacing: '0.02em' }}>
            Start your card →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ShowcaseGallery;
